// React
import type { RouteRecord } from 'vite-react-ssg';

import cmsRoutes from 'virtual:cms-routes';

// Views
import Home, { homePageLoader } from '@/views/Home/Home';
import News, { newsPageLoader } from '@/views/News/News';
import Contact, { contactLoader } from '@/views/Contact/Contact';
import OffresEtServices, { offresEtServicesLoader } from '@/views/OffresEtServices/OffresEtServices';
import { resources } from '@/locales/resources';
import App from '@/App';

// Utils
import { buildNestedRoutes } from '@/utils/buildNestedRoutes';
import { getContactSlug } from '@/utils/contactSlug';
import { getSlug } from '@/utils/localeSlug';
import type { CmsRouteEntry } from '@/utils/cmsRoutesData';
import NotFound, { notFoundLoader } from '@/views/NotFound/NotFound';
import RouteError from '@/views/RouteError/RouteError';
import { withMediaVariants } from '@/utils/media/withMediaVariants';

const NOT_FOUND_SLUG = '404';

/**
 * Slugs déjà couverts par des routes dédiées.
 * Les exclure évite un doublon de segment (ex. `actualites` sans composant) qui casse `/actualites`.
 */
function normalizeSlugPath(slug: string): string {
  return slug
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .join('/');
}

function excludePagesConflictingWithStaticRoutes(
  pages: CmsRouteEntry[],
  reservedSlugs: string[],
): CmsRouteEntry[] {
  const reserved = reservedSlugs
    .map(normalizeSlugPath)
    .filter(Boolean);

  return pages.filter((page) => {
    const norm = normalizeSlugPath(getSlug(page.slug));
    return !reserved.some(
      (res) => norm === res || norm.startsWith(`${res}/`),
    );
  });
}

const getChildren = (): RouteRecord[] => {
  const languages = Object.keys(resources) as Array<keyof typeof resources>;

  return languages.map((value) => {
    const { pages, newsPage, offresEtServices } = cmsRoutes[value];
    const contactSlug = getContactSlug(value);

    const reservedSlugs = [
      contactSlug,
      NOT_FOUND_SLUG,
      ...(newsPage ? [newsPage.slug] : []),
      ...(offresEtServices ? [offresEtServices.slug] : []),
    ];

    const pagesForNestedRoutes = excludePagesConflictingWithStaticRoutes(pages, reservedSlugs);
    const children = buildNestedRoutes(pagesForNestedRoutes);

    const staticRoutes: RouteRecord[] = [
      {
        index: true,
        Component: () => <Home />,
        loader: withMediaVariants(homePageLoader),
        handle: { breadcrumb: 'Accueil' },
      },
    ];

    if (newsPage) {
      const newsPaths = [
        newsPage.slug,
        ...Array.from({ length: newsPage.pageCount - 1 }, (_, i) => `${newsPage.slug}/page/${i + 2}`),
      ];

      newsPaths.forEach((path) => {
        staticRoutes.push({
          path,
          Component: () => <News />,
          loader: withMediaVariants(newsPageLoader),
          handle: { breadcrumb: newsPage.breadcrumb },
        });
      });
    }

    if (offresEtServices) {
      staticRoutes.push({
        path: offresEtServices.slug,
        Component: () => <OffresEtServices />,
        loader: withMediaVariants(offresEtServicesLoader),
        handle: { breadcrumb: offresEtServices.breadcrumb },
      });
    }

    staticRoutes.push({
      path: contactSlug,
      Component: () => <Contact />,
      loader: withMediaVariants(contactLoader),
      handle: { breadcrumb: resources[value].translation.contact.title },
    });

    return {
      path: value === 'fr-FR' ? '/' : `/${value}`,
      Component: App,
      errorElement: <RouteError />,
      children: [
        ...staticRoutes,
        ...children,
        // Page 404 générée au build (/404/, /de-DE/404/…) : c'est elle que CloudFront sert
        // pour une URL inconnue, avec un statut 404.
        {
          path: NOT_FOUND_SLUG,
          Component: () => <NotFound />,
          loader: withMediaVariants(notFoundLoader),
        },
        // Navigation côté client vers une URL inconnue
        {
          path: '*',
          Component: () => <NotFound />,
          loader: withMediaVariants(notFoundLoader),
        },
      ],
    };
  });
};

const publicRoutes: RouteRecord[] = getChildren();

export default [
  ...publicRoutes,
];
